// ShopPage.jsx — /shop
// Public merch storefront backed by Square Catalog. Reads the cached catalog
// written by the `syncShopCatalog` Cloud Function (Firestore `config/shopCatalog`).
// Square token is never exposed to the browser.
//
// This is Slice 1.1: grid + item modal + add-to-cart.
// Cart drawer (1.2), checkout (1.3), webhook mirror (1.4), fulfillment view
// (1.5) arrive in subsequent slices. The "Add to cart" button here already
// writes to localStorage via src/utils/cart.js so the drawer in 1.2 has data
// the moment it's built.
//
// POD items (tagged with the "Print on Demand" category in Square) display
// but show a "Coming soon" state — checkout will be wired once Printful
// integration lands.

import { useEffect, useState } from "react";
import { doc, onSnapshot } from "firebase/firestore";
import { getFunctions, httpsCallable } from "firebase/functions";
import { getApp } from "firebase/app";
import { getAuth, onAuthStateChanged } from "firebase/auth";
import { db } from "../../firebase";
import PanBar from "./components/PanBar";
import SiteNav from "./components/SiteNav";
import SiteFooter from "./sections/SiteFooter";
import { addToCart, formatPrice } from "../../utils/cart";
import CartDrawer, { CartBadgeButton, QtyStepper } from "../../components/CartDrawer";

// Site brand tokens (match /site Hero gradient so transitions feel continuous).
const BG = `radial-gradient(ellipse 140% 95% at 50% 0%,
  #1f59db 0%,
  #1a4b9e 30%,
  #0e2e46 70%,
  #081a2a 100%)`;

// Curated section order for the shop grid. Any Square subcategory name you
// want surfaced first goes here. Items whose categories aren't in this list
// still appear — they just render after the named sections in alphabetical
// order. Items tagged only with "Online Shop" land in "Everything else."
//
// To reorder: edit this array. To rename a section: rename the Square
// category in Dashboard — syncShopCatalog picks it up on the next run.
const SECTION_ORDER = [
  "Kandi",
];
const EVERYTHING_ELSE = "Everything else";
// Categories that are operational tags, not visual sections. "Online Shop"
// is the gate for being in the shop at all; "Print on Demand" is a
// fulfillment flag (rendered as a "Coming soon" badge on the card).
const SECTION_EXCLUDED = new Set(["Online Shop", "Print on Demand"]);

export default function ShopPage({ navigate }) {
  const [items, setItems] = useState(null);       // null = loading, [] = empty
  const [syncedAt, setSyncedAt] = useState(null);
  const [warning, setWarning] = useState(null);
  const [selected, setSelected] = useState(null); // item for modal
  const [addedToast, setAddedToast] = useState(null);
  const [resyncResult, setResyncResult] = useState(null);
  const [resyncing, setResyncing]       = useState(false);
  const [cartOpen, setCartOpen]         = useState(false);

  // Staff escape hatch: /shop?resync=1 forces an immediate sync without
  // waiting for the 15-min schedule. Waits for Firebase Auth to hydrate
  // from persistent storage before gating — otherwise currentUser is null
  // on fresh page load even when the user is signed in. Only runs for the
  // authenticated staff account (enforced server-side too).
  useEffect(() => {
    const sp = new URLSearchParams(window.location.search);
    if (sp.get("resync") !== "1") return;
    setResyncing(true);
    const auth = getAuth();
    const unsub = onAuthStateChanged(auth, (user) => {
      unsub(); // one-shot: we only want the first resolved state
      if (user?.email !== "jump@thejoyofpancakes.com") {
        setResyncResult({ error: "Sign in as staff first (go to / and log in), then hit /shop?resync=1" });
        setResyncing(false);
        return;
      }
      const fn = httpsCallable(getFunctions(getApp()), "syncShopCatalogNow");
      fn()
        .then((r) => setResyncResult(r.data))
        .catch((err) => setResyncResult({ error: err.message }))
        .finally(() => setResyncing(false));
    });
  }, []);

  // Live subscription — if syncShopCatalog runs while a visitor is on the
  // page, their view refreshes without a reload.
  useEffect(() => {
    const unsub = onSnapshot(doc(db, "config", "shopCatalog"), (snap) => {
      if (!snap.exists()) {
        setItems([]);
        return;
      }
      const data = snap.data();
      setItems(Array.isArray(data.items) ? data.items : []);
      setSyncedAt(data.syncedAt || null);
      setWarning(data.warning || null);
    }, (err) => {
      console.warn("ShopPage: catalog read failed", err);
      setItems([]);
    });
    return () => unsub();
  }, []);

  function handleAdd(item, variation, quantity) {
    addToCart({
      squareItemId: item.id,
      variationId:  variation.id,
      name:         item.name,
      variantName:  variation.name || null,
      priceCents:   variation.priceCents,
      currency:     variation.currency,
      imageUrl:     item.imageUrl,
      isPOD:        item.isPOD,
      quantity,
    });
    const qtyStr = quantity > 1 ? ` (×${quantity})` : "";
    setAddedToast(`Added "${item.name}${variation.name ? " — " + variation.name : ""}"${qtyStr} to cart`);
    setSelected(null);
    setTimeout(() => setAddedToast(null), 2800);
  }

  return (
    <div style={{ minHeight: "100vh", background: BG, fontFamily: "var(--jop-display)", color: "var(--jop-cream)" }}>

      {/* Public-site nav — same bar as /site, /events, /quote etc. so the
          shop isn't a dead end for cross-page browsing. */}
      <SiteNav />

      {/* Floating cart badge — fixed top-right, sits below the nav bar
          (56px tall) so it doesn't collide with the nav's own CTA. Pops in
          when there are items, hides entirely when empty. */}
      <div style={{
        position: "fixed",
        top: "calc(56px + 0.75rem)",
        right: "1.25rem",
        zIndex: 50,
      }}>
        <CartBadgeButton onClick={() => setCartOpen(true)} />
      </div>

      <div style={{ maxWidth: "1120px", margin: "0 auto", padding: "5rem 1.25rem 5rem" }}>

        {/* Header */}
        <div style={{ textAlign: "center", marginBottom: "2rem" }}>
          <PanBar width={72} height={3} margin="0 auto 1rem" />
          <h1 style={{
            fontFamily: "var(--jop-script)",
            fontSize: "clamp(2.4rem, 6vw, 3.6rem)",
            color: "var(--jop-gold)",
            fontWeight: 700,
            margin: "0 0 0.6rem",
            lineHeight: 1.05,
          }}>
            The Shop
          </h1>
          <p style={{ color: "rgba(255,218,173,0.75)", fontSize: "1rem", lineHeight: 1.55, maxWidth: "580px", margin: "0 auto" }}>
            Merch, stickers, prints, and the handmade kandi jewelry Wella makes between shows. A little piece of The Joy of Pancakes for your life.
          </p>
        </div>

        {/* Catalog warning (staff-visible signal when sync can't find the category) */}
        {warning && (
          <div style={{ maxWidth: "560px", margin: "0 auto 1.5rem", background: "rgba(252,179,31,0.12)", border: "1px solid rgba(252,179,31,0.4)", borderRadius: "10px", padding: "0.8rem 1rem", color: "var(--jop-gold)", fontSize: "0.85rem" }}>
            ⚠ {warning}
          </div>
        )}

        {/* Resync debug banner (staff only, triggered by ?resync=1) */}
        {(resyncing || resyncResult) && (
          <div style={{ maxWidth: "560px", margin: "0 auto 1.5rem", background: "rgba(74,164,225,0.12)", border: "1px solid rgba(74,164,225,0.4)", borderRadius: "10px", padding: "0.8rem 1rem", color: "#8ecff5", fontSize: "0.82rem", fontFamily: "monospace" }}>
            {resyncing && "⟳ Syncing from Square…"}
            {!resyncing && resyncResult && (
              <pre style={{ margin: 0, whiteSpace: "pre-wrap", wordBreak: "break-word" }}>{JSON.stringify(resyncResult, null, 2)}</pre>
            )}
          </div>
        )}

        {/* Loading / empty / grid */}
        {items === null ? (
          <div style={{ textAlign: "center", color: "rgba(255,218,173,0.55)", padding: "4rem 1rem" }}>Loading the shop…</div>
        ) : items.length === 0 ? (
          <EmptyState />
        ) : (
          groupItemsBySection(items).map(([sectionName, sectionItems]) => (
            <ShopSection
              key={sectionName}
              name={sectionName}
              items={sectionItems}
              onOpen={setSelected}
            />
          ))
        )}

        {/* Shop-wide disclosure — placing an order implies acceptance of our
            customer-facing policies. Kept subtle; linked to the canonical pages. */}
        <div style={{
          marginTop: "3rem",
          paddingTop: "1.2rem",
          borderTop: "1px solid rgba(255,218,173,0.12)",
          textAlign: "center",
          color: "rgba(255,218,173,0.55)",
          fontSize: "0.78rem",
          lineHeight: 1.6,
        }}>
          By placing an order you agree to our{" "}
          <a href="/shipping" style={{ color: "var(--jop-highlight-blue)", textDecoration: "underline", textUnderlineOffset: "2px" }}>Shipping & Returns</a>,{" "}
          <a href="/privacy" style={{ color: "var(--jop-highlight-blue)", textDecoration: "underline", textUnderlineOffset: "2px" }}>Privacy Policy</a>, and{" "}
          <a href="/terms" style={{ color: "var(--jop-highlight-blue)", textDecoration: "underline", textUnderlineOffset: "2px" }}>Terms of Service</a>.
        </div>
      </div>

      {/* Item detail modal */}
      {selected && (
        <ItemModal
          item={selected}
          onClose={() => setSelected(null)}
          onAdd={(variation, quantity) => handleAdd(selected, variation, quantity)}
        />
      )}

      {/* Cart drawer */}
      <CartDrawer open={cartOpen} onClose={() => setCartOpen(false)} />

      {/* "Added to cart" toast */}
      {addedToast && (
        <div style={{
          position: "fixed",
          bottom: "1.5rem",
          left: "50%",
          transform: "translateX(-50%)",
          background: "rgba(252,179,31,0.95)",
          color: "#1a0a12",
          fontWeight: 800,
          fontSize: "0.92rem",
          padding: "0.7rem 1.25rem",
          borderRadius: "999px",
          boxShadow: "0 10px 28px rgba(0,0,0,0.4)",
          zIndex: 300,
          maxWidth: "88vw",
          textAlign: "center",
        }}>
          ✓ {addedToast}
        </div>
      )}

      {/* Site footer — policies, socials, newsletter, business info. */}
      <SiteFooter />
    </div>
  );
}

// ── Section shelf ──────────────────────────────────────────────────────────
// A category header + its own product grid. Multiple shelves stack down the
// page, each feeling like its own small corner of the shop.
function ShopSection({ name, items, onOpen }) {
  return (
    <section style={{ marginBottom: "2.75rem" }}>
      <div style={{
        display: "flex",
        alignItems: "baseline",
        justifyContent: "center",
        gap: "0.9rem",
        marginBottom: "1.1rem",
        paddingBottom: "0.55rem",
        borderBottom: "1px solid rgba(255,218,173,0.18)",
      }}>
        <h2 style={{
          fontFamily: "var(--jop-script)",
          fontSize: "clamp(1.55rem, 3.4vw, 2rem)",
          color: "var(--jop-gold)",
          fontWeight: 700,
          margin: 0,
          lineHeight: 1.1,
          letterSpacing: "-0.01em",
        }}>
          {name}
        </h2>
        <span style={{
          color: "rgba(255,218,173,0.45)",
          fontSize: "0.78rem",
          fontWeight: 600,
          letterSpacing: "0.08em",
          textTransform: "uppercase",
        }}>
          {items.length} {items.length === 1 ? "item" : "items"}
        </span>
      </div>
      <div style={{
        display: "grid",
        // auto-fill (not auto-fit) keeps empty tracks reserved — so a
        // lone sticker or print doesn't stretch to fill a 4-column row.
        // Tracks cap at 300px on wide screens to keep cards human-scaled.
        gridTemplateColumns: "repeat(auto-fill, minmax(220px, 300px))",
        gap: "1.4rem",
        justifyContent: "center",
      }}>
        {items.map((item) => (
          <ProductCard key={item.id} item={item} onOpen={() => onOpen(item)} />
        ))}
      </div>
    </section>
  );
}

// ── Product grid card ──────────────────────────────────────────────────────
function ProductCard({ item, onOpen }) {
  const { lowestPrice, singlePrice } = priceRange(item.variations);
  return (
    <button
      onClick={onOpen}
      style={{
        background: "rgba(11,29,52,0.72)",
        border: "1px solid rgba(255,218,173,0.18)",
        borderRadius: "14px",
        overflow: "hidden",
        cursor: "pointer",
        textAlign: "left",
        display: "flex",
        flexDirection: "column",
        boxShadow: "0 10px 26px rgba(0,0,0,0.3)",
        transition: "transform 0.2s ease, box-shadow 0.2s ease",
        fontFamily: "inherit",
        color: "inherit",
        padding: 0,
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.transform = "translateY(-3px)";
        e.currentTarget.style.boxShadow = "0 16px 36px rgba(0,0,0,0.42), 0 0 18px rgba(252,179,31,0.2)";
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.transform = "translateY(0)";
        e.currentTarget.style.boxShadow = "0 10px 26px rgba(0,0,0,0.3)";
      }}
    >
      <div style={{
        width: "100%",
        aspectRatio: "1 / 1",
        background: item.imageUrl ? "#000" : "rgba(255,218,173,0.06)",
        display: "flex", alignItems: "center", justifyContent: "center",
        overflow: "hidden",
      }}>
        {item.imageUrl ? (
          <img src={item.imageUrl} alt={item.name} loading="lazy" style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }} />
        ) : (
          <div style={{ color: "rgba(255,218,173,0.35)", fontSize: "2rem" }}>🥞</div>
        )}
      </div>
      <div style={{ padding: "0.95rem 1rem 1rem", display: "flex", flexDirection: "column", gap: "0.35rem" }}>
        <div style={{ color: "white", fontWeight: 700, fontSize: "0.95rem", lineHeight: 1.3 }}>
          {item.name}
          {item.isPOD && <span style={{ marginLeft: "0.5rem", color: "rgba(255,218,173,0.55)", fontWeight: 600, fontSize: "0.7rem", textTransform: "uppercase", letterSpacing: "0.08em" }}>Coming soon</span>}
        </div>
        <div style={{ color: "var(--jop-gold)", fontWeight: 800, fontSize: "0.95rem" }}>
          {singlePrice != null ? formatPrice(singlePrice) : lowestPrice != null ? `from ${formatPrice(lowestPrice)}` : "—"}
        </div>
      </div>
    </button>
  );
}

// ── Item detail modal ──────────────────────────────────────────────────────
function ItemModal({ item, onClose, onAdd }) {
  const [variationId, setVariationId] = useState(item.variations?.[0]?.id || null);
  const [quantity, setQuantity]       = useState(1);
  const variation = item.variations.find(v => v.id === variationId) || item.variations[0];

  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = prev;
    };
  }, [onClose]);

  return (
    <>
      <style>{`
        .jop-shop-modal-backdrop {
          position: fixed; inset: 0;
          background: rgba(6,12,25,0.82);
          backdrop-filter: blur(6px);
          -webkit-backdrop-filter: blur(6px);
          z-index: 200;
          display: flex;
          align-items: flex-end;
          justify-content: center;
          animation: jopShopFade 0.22s ease;
        }
        .jop-shop-modal-panel {
          background: #0b1d34;
          color: var(--jop-cream);
          width: 100%;
          max-width: 540px;
          max-height: 92vh;
          overflow-y: auto;
          border-radius: 18px 18px 0 0;
          border-top: 4px solid var(--jop-gold);
          box-shadow: 0 -20px 60px rgba(0,0,0,0.55);
          scrollbar-width: thin;
          scrollbar-color: rgba(255,218,173,0.22) transparent;
        }
        .jop-shop-modal-panel::-webkit-scrollbar { width: 8px; }
        .jop-shop-modal-panel::-webkit-scrollbar-track { background: transparent; }
        .jop-shop-modal-panel::-webkit-scrollbar-thumb {
          background: rgba(255,218,173,0.22);
          border-radius: 8px;
          border: 2px solid transparent;
          background-clip: padding-box;
        }
        @media (min-width: 720px) {
          .jop-shop-modal-backdrop { align-items: center; }
          .jop-shop-modal-panel { border-radius: 18px; border: 1px solid rgba(252,179,31,0.35); border-top-width: 4px; }
        }
        @keyframes jopShopFade { from { opacity: 0; } to { opacity: 1; } }
      `}</style>

      <div className="jop-shop-modal-backdrop" onClick={onClose}>
        <div className="jop-shop-modal-panel" onClick={(e) => e.stopPropagation()}>

          {/* Header with close */}
          <div style={{ position: "sticky", top: 0, background: "linear-gradient(180deg, #0b1d34 0%, rgba(11,29,52,0.94) 100%)", padding: "0.9rem 1rem 0.8rem", display: "flex", justifyContent: "flex-end", zIndex: 2 }}>
            <button
              onClick={onClose}
              aria-label="Close"
              style={{ background: "transparent", border: "1px solid rgba(255,218,173,0.25)", color: "rgba(255,218,173,0.75)", width: "34px", height: "34px", borderRadius: "999px", fontSize: "1.1rem", cursor: "pointer", fontFamily: "inherit" }}
            >
              ×
            </button>
          </div>

          {/* Image */}
          <div style={{ width: "100%", aspectRatio: "1 / 1", background: "#000", overflow: "hidden" }}>
            {item.imageUrl ? (
              <img src={item.imageUrl} alt={item.name} style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }} />
            ) : (
              <div style={{ width: "100%", height: "100%", display: "flex", alignItems: "center", justifyContent: "center", color: "rgba(255,218,173,0.35)", fontSize: "3rem" }}>🥞</div>
            )}
          </div>

          <div style={{ padding: "1.25rem 1.25rem 1.75rem" }}>
            <h2 style={{ margin: "0 0 0.35rem", color: "white", fontSize: "1.35rem", fontWeight: 800, lineHeight: 1.25 }}>{item.name}</h2>
            <div style={{ color: "var(--jop-gold)", fontWeight: 800, fontSize: "1.15rem", marginBottom: "1rem" }}>
              {variation?.priceCents != null ? formatPrice(variation.priceCents, variation.currency) : "—"}
            </div>

            {item.description && (
              <p style={{ color: "var(--jop-cream)", fontSize: "0.92rem", lineHeight: 1.6, margin: "0 0 1.1rem", whiteSpace: "pre-wrap" }}>
                {item.description}
              </p>
            )}

            {/* Variation picker — only shown when there are ≥2 variations or the
                single variation has a meaningful name (i.e. not just "Regular"). */}
            {item.variations.length > 1 && (
              <div style={{ marginBottom: "1.1rem" }}>
                <div style={{ color: "rgba(255,218,173,0.55)", fontSize: "0.72rem", fontWeight: 700, letterSpacing: "0.1em", textTransform: "uppercase", marginBottom: "0.5rem" }}>
                  Choose
                </div>
                <div style={{ display: "flex", flexWrap: "wrap", gap: "0.45rem" }}>
                  {item.variations.map(v => (
                    <button
                      key={v.id}
                      onClick={() => setVariationId(v.id)}
                      style={{
                        background: v.id === variationId ? "var(--jop-gold)" : "transparent",
                        color: v.id === variationId ? "#1a0a12" : "var(--jop-cream)",
                        border: `1px solid ${v.id === variationId ? "var(--jop-gold)" : "rgba(255,218,173,0.3)"}`,
                        borderRadius: "999px",
                        padding: "0.35rem 0.85rem",
                        fontSize: "0.82rem",
                        fontWeight: 700,
                        cursor: "pointer",
                        fontFamily: "inherit",
                      }}
                    >
                      {v.name || "Option"}
                      {v.priceCents != null && v.priceCents !== variation.priceCents
                        ? ` · ${formatPrice(v.priceCents)}`
                        : ""}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Add to cart / POD disabled state */}
            {item.isPOD ? (
              <div style={{
                background: "rgba(255,218,173,0.08)",
                border: "1px dashed rgba(255,218,173,0.35)",
                borderRadius: "12px",
                padding: "1rem",
                textAlign: "center",
                color: "var(--jop-cream)",
                fontSize: "0.88rem",
                lineHeight: 1.5,
              }}>
                This one's print-on-demand through our fulfillment partner — we're wiring it up next. Check back soon.
              </div>
            ) : (
              <>
                {/* Qty picker — lives above the Add button so visitors can
                    pick N before adding instead of tapping Add N times. */}
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "0.75rem", gap: "0.75rem" }}>
                  <div style={{ color: "rgba(255,218,173,0.55)", fontSize: "0.72rem", fontWeight: 700, letterSpacing: "0.1em", textTransform: "uppercase" }}>
                    Quantity
                  </div>
                  <QtyStepper value={quantity} onChange={setQuantity} min={1} max={99} />
                </div>
                <button
                  onClick={() => variation && onAdd(variation, quantity)}
                  disabled={!variation || variation.priceCents == null}
                  style={{
                    width: "100%",
                    background: "var(--jop-gold)",
                    color: "#1a0a12",
                    border: "none",
                    borderRadius: "999px",
                    padding: "0.9rem",
                    fontSize: "1rem",
                    fontWeight: 800,
                    fontFamily: "var(--jop-display)",
                    cursor: "pointer",
                    boxShadow: "0 6px 22px rgba(252,179,31,0.35)",
                    letterSpacing: "0.02em",
                  }}
                >
                  Add {quantity > 1 ? `${quantity} ` : ""}to cart · {formatPrice((variation?.priceCents || 0) * quantity, variation?.currency)}
                </button>
              </>
            )}
          </div>
        </div>
      </div>
    </>
  );
}

// ── Empty state ──
function EmptyState() {
  return (
    <div style={{ textAlign: "center", padding: "3rem 1rem", color: "rgba(255,218,173,0.65)" }}>
      <div style={{ fontSize: "3rem", marginBottom: "0.75rem" }}>🪄</div>
      <div style={{ fontFamily: "var(--jop-script)", fontSize: "1.8rem", color: "var(--jop-gold)", marginBottom: "0.5rem" }}>Shop coming soon</div>
      <p style={{ fontSize: "0.95rem", lineHeight: 1.55, maxWidth: "440px", margin: "0 auto" }}>
        We're stocking the shelves. In the meantime, catch us at a market or on Twitch.
      </p>
    </div>
  );
}

// ── Helpers ──

// Group items into section shelves based on their Square subcategories.
// SECTION_ORDER is the allowlist of named sections — any item matching one
// or more of those appears only there (lets Dan cross-list intentionally,
// e.g., "Stickers" + "Featured"). Items that don't match any curated
// section fall into "Everything else," so legacy POS navigation categories
// like "Merchandise" stay invisible to shoppers without being lost.
// Returns [sectionName, items[]] tuples in render order.
function groupItemsBySection(items) {
  const buckets = new Map();
  const push = (key, value) => {
    if (!buckets.has(key)) buckets.set(key, []);
    buckets.get(key).push(value);
  };
  const curated = new Set(SECTION_ORDER);

  for (const item of items) {
    const cats = (item.categoryNames || []).filter((c) => !SECTION_EXCLUDED.has(c));
    const curatedMatches = cats.filter((c) => curated.has(c));
    if (curatedMatches.length > 0) {
      for (const cat of curatedMatches) push(cat, item);
    } else {
      push(EVERYTHING_ELSE, item);
    }
  }

  // Render order: SECTION_ORDER first (in the given order), then
  // "Everything else" last. Nothing else can create a section.
  const ordered = [];
  for (const name of SECTION_ORDER) {
    if (buckets.has(name)) ordered.push([name, buckets.get(name)]);
  }
  if (buckets.has(EVERYTHING_ELSE)) ordered.push([EVERYTHING_ELSE, buckets.get(EVERYTHING_ELSE)]);
  return ordered;
}

function priceRange(variations) {
  if (!variations || variations.length === 0) return { lowestPrice: null, singlePrice: null };
  const prices = variations.map(v => v.priceCents).filter(p => p != null);
  if (prices.length === 0) return { lowestPrice: null, singlePrice: null };
  const min = Math.min(...prices);
  const max = Math.max(...prices);
  return {
    lowestPrice: min,
    singlePrice: min === max ? min : null,
  };
}
